/**
 * Clinical-identifier redaction for the Feedback Widget payload.
 *
 * The widget POSTs user feedback DIRECTLY to the external Feedback Loop worker
 * (feedback.oneighty.cloud), so the server-side scrubber never sees it — the same
 * egress boundary that BL-001 hardened for the passive crash client. SEER is
 * pervasively child-data: an authenticated route is `/children/<uuid>/…`, so the
 * auto-captured page URL alone carries a child/org identifier on every report.
 *
 * Strategy = exact parity with `public/bugsnag-client.js` (bsRedactText / bsRedactUrl):
 * strip UUIDs and long numeric path IDs locally, and reuse the MAINTAINED server
 * rule set (`src/lib/bugsnag/scrub-pii.ts`) for AU phone / NDIS / Medicare / email /
 * DOB so the phone/NDIS/Medicare regexes have a single source of truth (guarded by
 * scrub-pii.test.ts). Free-text NAMES remain the only residual — a regex can't catch
 * them; that is the policy-gated BL-001 part 2 concern, identical to the crash client.
 *
 * Guarded by feedback-redact.test.ts.
 */
import { scrubPii } from "@/lib/bugsnag/scrub-pii";

const UUID_RE =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

/**
 * Strip clinical identifiers from free text. Order mirrors bsRedactText:
 * UUID first, then the maintained phone/NDIS/Medicare/email/DOB rules, then any
 * remaining long numeric path IDs (after scrubPii so a bare 10-digit value is
 * labelled [MEDICARE-REDACTED], not silently flattened to /:id).
 */
export function redactText(t: string | null | undefined): string {
  if (!t) return t ?? "";
  try {
    const withoutUuid = String(t).replace(UUID_RE, ":id");
    const scrubbed = scrubPii(withoutUuid) ?? withoutUuid;
    return scrubbed.replace(/\/\d{6,}(?=\/|$|\?)/g, "/:id");
  } catch {
    return "";
  }
}

/**
 * Redact a URL: drop the fragment and the query CONTENT (it can carry tokens,
 * emails or search terms) while keeping a `?…` marker so triage knows a query was
 * present, then run the path through redactText. Mirrors bsRedactUrl.
 */
export function redactUrl(u: string | null | undefined): string {
  if (!u) return u ?? "";
  try {
    let s = String(u);
    const hashAt = s.indexOf("#");
    if (hashAt !== -1) s = s.slice(0, hashAt);
    const qAt = s.indexOf("?");
    const hadQuery = qAt !== -1;
    if (hadQuery) s = s.slice(0, qAt);
    s = redactText(s);
    return hadQuery ? `${s}?…` : s;
  } catch {
    return "";
  }
}
